/**
 * Dati dei grafici del confronto (V31). Ogni giocatore scelto è una serie
 * con chiave `g0`, `g1`… nell'ordine di scelta: la chiave dà il colore, e
 * così un giocatore ha lo stesso colore in tutti i grafici.
 */

import type { ChartConfig } from '@/components/ui/chart';
import type { ContatoriEventi, RigaClassifica } from '../statistiche/statistiche.js';
import type { AttributoColonna, GiocatoreConfronto } from './tipi.js';

const COLORI_TEMA = 5;

/**
 * I primi cinque giocatori prendono i colori del tema; i successivi le loro
 * varianti sfumate verso lo sfondo, poi verso il testo, così restano leggibili
 * in chiaro e in scuro. Dal sedicesimo i colori si ripetono.
 */
export function coloreSerie(indice: number): string {
  const base = `var(--chart-${(indice % COLORI_TEMA) + 1})`;
  const giro = Math.floor(indice / COLORI_TEMA) % 3;
  if (giro === 0) return base;
  const verso = giro === 1 ? 'var(--background)' : 'var(--foreground)';
  return `color-mix(in oklab, ${base} 55%, ${verso})`;
}

export interface Serie {
  chiave: string;
  id: string;
  nome: string;
}

export function serieGiocatori(
  scelti: readonly string[],
  giocatori: readonly { id: string; nome: string; cognome: string }[],
): Serie[] {
  return scelti.flatMap((id, i) => {
    const g = giocatori.find((x) => x.id === id);
    return g ? [{ chiave: `g${i}`, id, nome: `${g.cognome} ${g.nome}` }] : [];
  });
}

export function configSerie(serie: readonly Serie[]): ChartConfig {
  return Object.fromEntries(
    serie.map((s) => [s.chiave, { label: s.nome, color: coloreSerie(Number(s.chiave.slice(1))) }]),
  );
}

/** Gli attributi che tutti i giocatori confrontati hanno rilevato almeno una volta. */
export function attributiComuni<T extends { id: string }>(
  attributi: readonly T[],
  giocatori: readonly GiocatoreConfronto[],
): T[] {
  if (giocatori.length === 0) return [];
  return attributi.filter((a) => giocatori.every((g) => g.valori[a.id] != null));
}

/** Gli attributi che almeno uno dei giocatori confrontati ha rilevato: quelli con un andamento. */
export function attributiRilevati<T extends { id: string }>(
  attributi: readonly T[],
  giocatori: readonly GiocatoreConfronto[],
): T[] {
  const rilevati = new Set(giocatori.flatMap((g) => g.storico.map((m) => m.attributoId)));
  return attributi.filter((a) => rilevati.has(a.id));
}

/**
 * L'andamento di un attributo: una riga per giorno in cui almeno un giocatore
 * lo ha rilevato, un valore per serie. Due misurazioni dello stesso giorno
 * fanno la media; chi quel giorno non ha misura resta vuoto.
 */
export function righeAndamentoAttributo(
  attributoId: string,
  serie: readonly Serie[],
  giocatori: readonly GiocatoreConfronto[],
): Record<string, string | number | null>[] {
  const perGiorno = new Map<string, Map<string, number[]>>();
  for (const s of serie) {
    const g = giocatori.find((x) => x.id === s.id);
    for (const m of g?.storico ?? []) {
      if (m.attributoId !== attributoId) continue;
      const giorno = perGiorno.get(m.data) ?? new Map<string, number[]>();
      giorno.set(s.chiave, [...(giorno.get(s.chiave) ?? []), m.valore]);
      perGiorno.set(m.data, giorno);
    }
  }
  return [...perGiorno]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, giorno]) => {
      const riga: Record<string, string | number | null> = { data };
      for (const s of serie) {
        const valori = giorno.get(s.chiave);
        riga[s.chiave] = valori
          ? Math.round(valori.reduce((t, v) => t + v, 0) / valori.length)
          : null;
      }
      return riga;
    });
}

/**
 * Una scala dal minimo al massimo dei valori mostrati, allargati al multiplo
 * del passo e tenuti nei limiti: valori vicini tra loro non restano
 * schiacciati in una fascia stretta della scala intera. Senza valori, i
 * limiti; con un valore solo, un passo per parte.
 */
export function scalaMinMax(
  righe: readonly Record<string, string | number | null>[],
  serie: readonly Serie[],
  { passo, limiti }: { passo: number; limiti: [number, number] },
): [number, number] {
  const [basso, alto] = limiti;
  const valori = righe.flatMap((r) =>
    serie.flatMap((s) => (typeof r[s.chiave] === 'number' ? [r[s.chiave] as number] : [])),
  );
  if (valori.length === 0) return Number.isFinite(alto) ? [basso, alto] : [basso, basso + passo];
  let min = Math.max(basso, Math.floor(Math.min(...valori) / passo) * passo);
  let max = Math.min(alto, Math.ceil(Math.max(...valori) / passo) * passo);
  if (min === max) {
    min = Math.max(basso, min - passo);
    max = Math.min(alto, max + passo);
  }
  return [min, max];
}

/**
 * Tacche regolari per una scala: il passo più piccolo fra 1, 2, 5, 10, 20,
 * 25, 50 e 100 che non dà più di cinque intervalli, e gli estremi allargati
 * al suo multiplo. Senza, Recharts mette tacche irregolari (0, 2, 4, 7).
 */
export function tacche([min, max]: [number, number]): number[] {
  const passo = [1, 2, 5, 10, 20, 25, 50, 100].find((p) => (max - min) / p <= 5) ?? 100;
  const da = Math.floor(min / passo) * passo;
  const a = Math.ceil(max / passo) * passo;
  return Array.from({ length: (a - da) / passo + 1 }, (_, i) => da + i * passo);
}

/** La scala degli attributi: min e max a multipli di 5, dentro 1-100. */
export const SCALA_ATTRIBUTI = { passo: 5, limiti: [1, 100] as [number, number] };
/** Conteggi degli eventi, un'unità alla volta, da zero in su. */
export const SCALA_CONTEGGI = { passo: 1, limiti: [0, Infinity] as [number, number] };
/** Minuti di una partita, a decine. */
export const SCALA_MINUTI = { passo: 10, limiti: [0, Infinity] as [number, number] };
/** Voto di una partita, da 1 a 10. */
export const SCALA_VOTO = { passo: 1, limiti: [1, 10] as [number, number] };

export function scalaAttributi(
  righe: readonly Record<string, string | number | null>[],
  serie: readonly Serie[],
): [number, number] {
  return scalaMinMax(righe, serie, SCALA_ATTRIBUTI);
}

/** Una riga per attributo, un valore per serie; il vuoto resta vuoto. */
export function righeRadar(
  attributi: readonly AttributoColonna[],
  serie: readonly Serie[],
  giocatori: readonly GiocatoreConfronto[],
): Record<string, string | number | null>[] {
  return attributi.map((a) => {
    const riga: Record<string, string | number | null> = { attributo: a.nome };
    for (const s of serie) {
      riga[s.chiave] = giocatori.find((g) => g.id === s.id)?.valori[a.id] ?? null;
    }
    return riga;
  });
}

export type Evento =
  | 'presenze'
  | 'gol'
  | 'assist'
  | 'tiri'
  | 'tiriSpecchio'
  | 'tiriFuori'
  | 'passaggi'
  | 'passaggiCompletati'
  | 'passaggiSbagliati'
  | 'palleConquistate'
  | 'pallePerse'
  | 'autogol'
  | 'gialli'
  | 'rossi';

/** Conteggi delle partite: stanno sullo stesso asse. Minuti e voto hanno il loro. */
export const EVENTI: { chiave: Evento; etichetta: string }[] = [
  { chiave: 'presenze', etichetta: 'Presenze' },
  { chiave: 'gol', etichetta: 'Gol' },
  { chiave: 'assist', etichetta: 'Assist' },
  { chiave: 'tiri', etichetta: 'Tiri' },
  { chiave: 'tiriSpecchio', etichetta: 'Tiri in porta' },
  { chiave: 'tiriFuori', etichetta: 'Tiri fuori' },
  { chiave: 'passaggi', etichetta: 'Passaggi' },
  { chiave: 'passaggiCompletati', etichetta: 'Passaggi riusciti' },
  { chiave: 'passaggiSbagliati', etichetta: 'Passaggi sbagliati' },
  { chiave: 'palleConquistate', etichetta: 'Palle recuperate' },
  { chiave: 'pallePerse', etichetta: 'Palle perse' },
  { chiave: 'autogol', etichetta: 'Autogol' },
  { chiave: 'gialli', etichetta: 'Gialli' },
  { chiave: 'rossi', etichetta: 'Rossi' },
];

export const EVENTI_PREDEFINITI: Evento[] = ['presenze'];

/** Gli eventi scelti vivono nella query string; senza, quelli predefiniti. */
export function eventiDaQuery(valore: string | null): Evento[] {
  if (valore == null) return EVENTI_PREDEFINITI;
  const scelti = new Set(valore.split(','));
  return EVENTI.map((e) => e.chiave).filter((c) => scelti.has(c));
}

function valoreEvento(r: RigaClassifica, e: Evento): number;
function valoreEvento(r: ContatoriEventi, e: Exclude<Evento, 'presenze'>): number;
function valoreEvento(r: ContatoriEventi & { presenze?: number }, e: Evento): number {
  if (e === 'tiri') return r.tiriSpecchio + r.tiriFuori;
  if (e === 'passaggi') return r.passaggiCompletati + r.passaggiSbagliati;
  if (e === 'presenze') return r.presenze ?? 0;
  return r[e];
}

/** Una riga per evento, nell'ordine di `EVENTI`, un valore per serie. */
export function righeEventi(
  eventi: readonly Evento[],
  serie: readonly Serie[],
  classifica: readonly RigaClassifica[],
): Record<string, string | number>[] {
  return EVENTI.filter((e) => eventi.includes(e.chiave)).map((e) => {
    const riga: Record<string, string | number> = { evento: e.etichetta };
    for (const s of serie) {
      const r = classifica.find((g) => g.id === s.id);
      riga[s.chiave] = r ? valoreEvento(r, e.chiave) : 0;
    }
    return riga;
  });
}

/** Una riga per giocatore, colorata con la sua serie: minuti o media voto. */
export function righePerGiocatore(
  serie: readonly Serie[],
  classifica: readonly RigaClassifica[],
  campo: 'minuti' | 'votoMedio',
): { chiave: string; valore: number | null; fill: string }[] {
  return serie.map((s) => ({
    chiave: s.chiave,
    valore: classifica.find((g) => g.id === s.id)?.[campo] ?? null,
    fill: `var(--color-${s.chiave})`,
  }));
}

/** Eventi partita per partita, da `/statistiche/giocatori/partite`. */
export interface PartiteGiocatori {
  partite: { id: string; data: string; avversario: string }[];
  giocatori: {
    id: string;
    partite: (ContatoriEventi & {
      partitaId: string;
      /** In distinta: conta come presenza. */
      presente: boolean;
      minuti: number;
      /** Solo per chi legge i voti. */
      voto?: number | null;
    })[];
  }[];
}

/**
 * L'andamento di un evento: una riga per partita, un valore per serie. Chi
 * in quella partita non è entrato né ha un evento resta vuoto, e la linea
 * lo scavalca. Le presenze, che partita per partita varrebbero uno o
 * niente, si sommano nel tempo: ogni partita ha il totale fin lì.
 */
export function righeAndamentoEvento(
  evento: Evento,
  serie: readonly Serie[],
  dati: PartiteGiocatori,
): Record<string, string | number | null>[] {
  const totali = new Map<string, number>();
  return dati.partite.map((p) => {
    const riga: Record<string, string | number | null> = {
      partita: p.id,
      data: p.data,
      avversario: p.avversario,
    };
    for (const s of serie) {
      const r = dati.giocatori
        .find((g) => g.id === s.id)
        ?.partite.find((x) => x.partitaId === p.id);
      if (evento === 'presenze') {
        const totale = (totali.get(s.chiave) ?? 0) + (r?.presente ? 1 : 0);
        totali.set(s.chiave, totale);
        riga[s.chiave] = totale;
      } else {
        riga[s.chiave] = r ? valoreEvento(r, evento) : null;
      }
    }
    return riga;
  });
}

/** Minuti o voto partita per partita; chi non ha giocato, o non ha il voto, resta vuoto. */
export function righeAndamentoPartita(
  campo: 'minuti' | 'voto',
  serie: readonly Serie[],
  dati: PartiteGiocatori,
): Record<string, string | number | null>[] {
  return dati.partite.map((p) => {
    const riga: Record<string, string | number | null> = {
      partita: p.id,
      data: p.data,
      avversario: p.avversario,
    };
    for (const s of serie) {
      const r = dati.giocatori
        .find((g) => g.id === s.id)
        ?.partite.find((x) => x.partitaId === p.id);
      riga[s.chiave] = campo === 'minuti' ? (r?.presente ? r.minuti : null) : (r?.voto ?? null);
    }
    return riga;
  });
}

export const PERIODI = ['mese', 'trimestre', 'stagione'] as const;
export type Periodo = (typeof PERIODI)[number] | 'personalizzato';

export const ETICHETTA_PERIODO: Record<(typeof PERIODI)[number], string> = {
  mese: 'Mese',
  trimestre: 'Trimestre',
  stagione: 'Stagione',
};

/** Il periodo scelto vive nella query string; senza, tutta la stagione. */
export function periodoDaQuery(q: URLSearchParams): Periodo {
  const p = q.get('periodo');
  if (p === 'personalizzato') return q.get('da') && q.get('a') ? p : 'stagione';
  return p === 'mese' || p === 'trimestre' ? p : 'stagione';
}

/** Lo stesso giorno di `mesi` mesi prima; se quel mese è più corto, il suo ultimo giorno. */
function mesiPrima(data: string, mesi: number): string {
  const [y, m, d] = data.split('-').map(Number) as [number, number, number];
  const ultimo = new Date(Date.UTC(y, m - 1 - mesi + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 - mesi, Math.min(d, ultimo))).toISOString().slice(0, 10);
}

/**
 * Gli estremi del periodo, da mostrare e da chiedere: l'ultimo mese e gli
 * ultimi tre fino a oggi, la stagione intera, o quelli scelti a mano.
 */
export function intervalloPeriodo(
  periodo: Periodo,
  stagione: { da: string; a: string },
  oggi: string,
  scelto?: { da: string; a: string },
): { da: string; a: string } {
  if (periodo === 'personalizzato' && scelto) return scelto;
  if (periodo === 'mese') return { da: mesiPrima(oggi, 1), a: oggi };
  if (periodo === 'trimestre') return { da: mesiPrima(oggi, 3), a: oggi };
  return stagione;
}

/**
 * Il pallino di una linea: nessuno, tranne quando la serie ha un valore solo,
 * che senza pallino non disegnerebbe nulla.
 */
export function pallinoSeSolo(
  righe: readonly Record<string, unknown>[],
  chiave: string,
): false | { r: number; fill: string } {
  const valori = righe.filter((r) => r[chiave] != null).length;
  return valori === 1 ? { r: 3, fill: `var(--color-${chiave})` } : false;
}

/** Le voci del tooltip dal valore più alto al più basso; quelle senza valore in fondo. */
export function ordinaPerValore<T extends { value?: unknown }>(voci: readonly T[]): T[] {
  const n = (v: T) => (typeof v.value === 'number' ? v.value : -Infinity);
  return [...voci].sort((a, b) => n(b) - n(a));
}

/** La classe di una serie, che `LegendaEvidenziabile` cerca per evidenziarla. */
export const classeSerie = (chiave: string) => `serie-${chiave}`;
